import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { CalculatedMetricBreakdownService } from './calculated-metric-breakdown.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CalculatedMetricsBreakdownData } from './calculated-metric-breakdown.model';
import { ModuleManagerMetricInfo } from 'src/shared/models/module-manager/module-manager-settings.models';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-calculated-metric-breakdown',
    templateUrl: './calculated-metric-breakdown.component.html',
    styleUrls: ['./calculated-metric-breakdown.component.scss']
})
export class CalculatedMetricBreakdownComponent implements OnInit {
  @Input() moduleManager: ModuleManager;
  @Input() moduleKey: string;
  @Input() metrics: ModuleManagerMetricInfo[] = [];
  @ViewChild("searchBox", { read: ElementRef }) searchBox: ElementRef<HTMLInputElement>;
  @ViewChild('calaculatedMetricsBreakdownGrid') agGrid: AgGridAngular;

    public data: CalculatedMetricsBreakdownData;
    public filterValue = "";
    public showFilter = false;
    public searchPlaceholder = "";
    public isNoData = false; // Boolean indicating the data load was empty.
    dataLoadSuccess = false;
    localeText = {};
    _lastLoaded = null; // Marks when the data was last loaded.  Used by onSettingsOpened to avoid reloading if it's been less than a minute.
    isFirstOpen = true;
    agGridLocalizationStrings = null; // Stores key/value pairs of strings used within agGrid for localization.
    fetchPromise = null; // The promise from fetchModuleData is stored so pending requests can be canceled if settings change.
    isFirstLoad = true; // Used for logging in endpoint
    isRequestingLocalizations = false;
    loadingStates = { config: true, data: true, localizations: true }; // Tracks loading for spinner.  Update through setLoadingState()
    localizationStrings = null;
    sortModel = null; // The sorted column state of the ag-grid.
    stateSaveTimer = null; // Used to debounce the save states

    // These two variables are bounded versions of the same function names from the factory.
    fetchModuleData;
    fetchExportData;

    didLastFetchFail = false;
    gridOptions: GridOptions = null;
    isFetching = false;
    isFirstDataLoaded = false;
    isGridReady = false;
    isNoMetric = false;



    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private service: CalculatedMetricBreakdownService,
        public configService: CvConfigurationService,
    ) {
        this.configureAgGrid();
    }

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        await this.loadLocalizations();
        this.fetchModuleData = this.service.fetchModuleData.bind(this.service, this.moduleKey);
        this.fetchExportData = this.service.fetchExportData.bind(this.service, this.moduleKey);

        this.moduleManager.customSettingsAdded = true;

        // PM is standardizing widget load which includes hiding the date range
        // Doesn't apply to CXOne
        if(this.configService.isCxOne)
            this.moduleManager.container.showDateRange = true;
        else
            this.moduleManager.showLoader = true;

        this.moduleManager.setOptions({
            onSettingsApplied: (settings) => {
                this.setLoadingState("config", false);
                this.setLoadingState("data", true);
                this.abortGetData();
                this.getData(false);
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Default,
            onRefresh: (isAutoRefresh) => {
                if (!isAutoRefresh) {
                    this.setLoadingState("data", true);
                }
                return this.getData(isAutoRefresh);
            },
            canRefresh: () => !this.isFetching && !this.isLoading(),
            onExport: format => this.fetchExportData(format),
        });

        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");
    }

    getFormulaData() {
        return this.data;
    }

    setLoadingState(subState, value) {
        this.loadingStates[subState] = value;
        this.moduleManager.showLoader = this.isLoading(subState);
    }

    private getData(isAutoRefresh) {
        if (!this.isFetching) {
            this.isFetching = true;
            this.fetchPromise = this.fetchModuleData({ isAutoRefresh: isAutoRefresh, isFirstLoad: this.isFirstLoad });
            this.fetchPromise.then((data) => {
                this.isFirstLoad = false;
                this.fetchPromise = null;
                delete data["BackendMessages"];

                this.data = data;

                // Error 1 = no metric
                if (this.data.Error == 1) {
                    this.didLastFetchFail = false;
                    this.moduleManager.showLoader = false;
                    this.isNoMetric = true;
                    this.setLoadingState("data", false);
                    return;
                }

                this.isNoMetric = false;

                if (this.isGridReady && this.agGrid?.api)
                    this.agGrid?.api.setGridOption('rowData',this.data.BreakdownMetrics);

                this.setLoadingState("data", false);
                this.didLastFetchFail = false;
                this.isFirstDataLoaded = true;
                this.moduleManager.container.showDateRange = true;
                this.moduleManager.showLoader = false;
                this.moduleManager.setAutoRefreshReady();
            }).catch((err) => {
                console.error("Calculated Metric Breakdown: Error fetching metric data. Status code from $http:", err.status, "Status code from data:", err.data && err.data.status_code);
                this.didLastFetchFail = true; // Could be used to update the View.
                this.moduleManager.handleLoadError(err, isAutoRefresh);
            }).finally(() => {
                this.isFetching = false;
            });
        }
    }

    // The primary scenario of abort: When this module's settings are changed.
    abortGetData() {
        if (this.isFetching) {
            this.fetchPromise.abort();
            this.isFetching = false;
        }
    }

    /**
     * Loads the localizations for this module, then sets up the grid.
     */
    private async loadLocalizations() {
        try {
            this.localizationStrings = await this.cvLocalizationService.GetSection("Calculated_Metric_Breakdown");
        } finally {
            this.setLoadingState("localizations", false);
            this.isRequestingLocalizations = false;
        }
    }

    /**
     * Returns the localization for the provided Key, or defaultValue if missing.
     * @param key String's Key from the *.Language Section for this module
     * @param defaultValue String returned if localizations didn't load, or the key is missing.
     */
    private getLocalizationString(key: string, defaultValue: string): string {
        if (this.localizationStrings?.has(key))
            return this.localizationStrings.get(key);
        return defaultValue;
    }

    public applyFilter() {
        if (this.agGrid.api) {
            this.filterValue = this.filterValue;
            this.agGrid?.api.setQuickFilter(this.filterValue.trim());

            if (this.showFilter && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0) {
                this.updateNoRowsMessage();
                this.agGrid?.api?.showNoRowsOverlay();
            } else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    }

    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    };

    /**
       * Toggle whether to show or hide the search bar
       */
    public toggleOpenSearch() {
        this.showFilter = !this.showFilter;
        if (this.showFilter) {
            this.focusOnSearch();
        } else {
            this.clearFilter();
        }
    }

    updateNoRowsMessage() {
        if (this.configService.isCxOne) {
            this.gridOptions.overlayNoRowsTemplate = `<div class="error-container" style="padding-top: 48px;">
            <svg class="mb-2">
                <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
            </svg>
            <p class="cxone-error-text">
                ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
            </p>
        </div>`;
        } //This message will display just for CXOne.
    }

    /**
         * Change input focus to the search bar.
         */
    async focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.configService.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            } else {
                this.searchBox?.nativeElement?.focus();
            }
        }, 200);
    }

    /**
     * This function is where the ag-grid gridOptions is configured.
     */
    public async configureAgGrid() {
        // This function assumes the agGridLocalizationStrings and localizationStrings have been set.
        // Since the backend is returning the real Metric object, the abbreviation (MetricName if empty) is returned for the value of that cell.
        function childMetricNameValueGetter(params) {
            var childMetric = params.data.ChildMetric;
            var childAbbr = childMetric.Abbreviation || "";
            var customMetricName = childMetric.MetricCustomName || "";
            if (customMetricName && customMetricName.trim().length > 1) {
                return `${customMetricName.toUpperCase()}*`;
            } else if (childAbbr && childAbbr.trim().length > 1)
                return childAbbr.toUpperCase();
            else {
                return childMetric.MetricName.toUpperCase();
            }
        }

        function childMetricValueGetter(params) {
            var childMetricValue = params.data.ChildMetricData;
            return childMetricValue.MetricDisplayValue;
        }

        // Since the value represented in the Metric column can be the abbreviation, this tooltip getter returns the actual MetricName
        function metricNameTooltipGetter(params) {
            return params.data.ChildMetric.MetricName;
        }

        this.gridOptions = this.agGridHelperService.createGridOptions({
            animateRows: true,
            context: {
                getMetric: params => params.data.BreakdownMetrics,
            },
            defaultColDef: {
                resizable: true,
                sortable: false,
                suppressMenu: true,
                suppressMovable: true,
                tooltipValueGetter: (params) => {
                    return params.valueFormatted || params.value;
                },
                icons: this.configService.isCxOne ? {
                    sortAscending: '<div class="sortIcons"><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    sortDescending: '<div class="sortIcons"><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                    menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                    columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
                }: null,
            },
            colResizeDefault: "shift",
            columnDefs: [
                {
                    colId: 'ChildMetric',
                    field: 'ChildMetric',
                    comparator: (valueA, valueB) => {
                        return valueA.localeCompare(valueB, undefined, { sensitivity: "accent" });
                    },
                    headerName: this.getLocalizationString('Metric', 'Metric'),
                    tooltipValueGetter: metricNameTooltipGetter,
                    valueGetter: childMetricNameValueGetter,
                    cellClass: params => params.data.ChildMetric.MetricID == this.data.ParentMetric.MetricID ? 'parent-metric' : 'child-metric',
                },
                {
                    colId: 'ChildMetricData',
                    field: "ChildMetricData",
                    headerName: this.getLocalizationString('Value', 'Value'),
                    valueGetter: childMetricValueGetter,
                    valueFormatter: (params: ValueFormatterParams) => params?.node?.data?.ChildMetricData.MetricDisplayValue,
                    sort: null,
                    type: 'metricColumn',
                    cellClass: params => params.data.ChildMetricData.MetricID == this.data.ParentMetric.MetricID ? 'parent-metric-data' : 'child-metric-data',
                },
            ],
            enableBrowserTooltips: true,
            //localeText: this.agGridLocalizationStrings,
            onGridReady: (agEvent) => {
                agEvent.api.setRowData(this.data?.BreakdownMetrics);
                agEvent.api.sizeColumnsToFit();
            },
            onGridSizeChanged: agEvent => agEvent.api.sizeColumnsToFit(),
            onSortChanged: agEvent => this.queueStateSave(),
            getRowId: data => data.data.Index,
            rowData: [],
            suppressRowDeselection: true,
            suppressContextMenu: true,
            suppressScrollOnNewData: true,
        }, {
            enableCopy: true
        });
        this.isGridReady = true;
    }

    /**
     * This function debounces state saves to 1s.
     */
    queueStateSave() {
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        this.stateSaveTimer = window.setTimeout(() => {
            this.moduleManager.saveState("sortedColumn");
            this.stateSaveTimer = null;
        }, 1000);
    }

    /**
         * If subState isn't specified, this function returns the cumulative result of all the loadingStates.  If any are loading, it returns true.
         * If subState is specified, only the loading state of that subState is returned.
         */
    isLoading(subState?) {
        if (!subState) {
            var loading = Object.keys(this.loadingStates).reduce((accumulator, subState) => {
                return accumulator || this.loadingStates[subState];
            }, false);
            return loading;
        } else {
            if (this.loadingStates[subState] !== undefined) {
                return this.loadingStates[subState];
            } else {
                return false;
            }
        }
    }
}
